import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DOMPurify from "dompurify";
import Modal from "react-modal";
import { Container, Row, Col } from "react-bootstrap";
import CommonStructured from "../../components/common-components/CommonStructured";
import HelmetWrapper from "../../HelmetWrapper";
import { selectUser } from "../../components/auth/auth.selector";
import CmnFilters from "../../components/lead-management/common/CmnFilters";
import ModalHeadAndClose from "../../components/payouts-wtf/common/ModalHeadAndClose";
import AddonCategoriesLists from "../../components/addons/addon-categories/AddonCategoriesLists";
import AddonCategoryForm from "../../components/addons/addon-categories/AddonCategoryForm";
import CarryItemsList from "../../components/addons/addon-categories/CarryItemsList";
import CarryItemForm from "../../components/addons/addon-categories/CarryItemForm";
import BenefitsList from "../../components/addons/addon-categories/BenefitsList";
import BenefitsForm from "../../components/addons/addon-categories/BenefitsForm";
import {
  fetchAddonCategoriesData,
  fetchAddonCarryItemsData,
  fetchAddonBenefitsData,
} from "../../redux/plans-reducers/plansSlice";
import "../../styles/addons/addon-categories.css";
import { NoImage } from "../../exports/Images";

const TABS = [
  { id: "categories", label: "Categories" },
  { id: "carry_items", label: "Carry Items" },
  { id: "benefits", label: "Benefits" },
];

const AddonsCategories = () => {
  const dispatch = useDispatch();
  const userInfoData = useSelector(selectUser);
  const {
    addonCategories,
    addonCategoriesLoading,
    addonCarryItemsLoading,
    addonBenefitsLoading,
  } = useSelector((state) => state.plans);

  const [activeTab, setActiveTab] = useState("categories");
  const [isPage, setIsPage] = useState(0);
  const [pageLimit, setPageLimit] = useState(10);

  const iniAllFilter = {
    isActive: "",
    searchString: "",
  };
  const [allFilters, setAllFilters] = useState(iniAllFilter);
  const iniFilters = {
    page: isPage + 1,
    limit: pageLimit || 10,
    status: allFilters.isActive?.value || "",
  };
  const [filters, setFilters] = useState(iniFilters);
  const [searchFilterApplied, setSearchFilterApplied] = useState(false);

  const iniModValues = {
    isModOpen: false,
    modName: "",
    rowData: "",
  };
  const [modValues, setModValues] = useState(iniModValues);

  // Category options for dropdown in forms
  const categoryOptions =
    addonCategories?.data?.map((cat) => ({
      value: cat?.uid || "",
      label: cat?.name || "",
    })) || [];

  const onClickSearch = () => {
    setIsPage(0);
    if (allFilters.searchString) {
      setSearchFilterApplied(true);
      setFilters((prevs) => ({ ...prevs, search: allFilters.searchString }));
    }
  };

  const onResetFilters = () => {
    setAllFilters(iniAllFilter);
    setFilters(iniFilters);
  };

  const onCloseModal = () => {
    setModValues(iniModValues);
  };

  const getAddonsCategoriesData = (filters) => {
    dispatch(
      fetchAddonCategoriesData({
        filters: filters,
        token: userInfoData?.token,
      })
    );
  };

  const getCarryItemsData = (filters) => {
    dispatch(
      fetchAddonCarryItemsData({
        filters: filters,
        token: userInfoData?.token,
      })
    );
  };

  const getBenefitsData = (filters) => {
    dispatch(
      fetchAddonBenefitsData({
        filters: filters,
        token: userInfoData?.token,
      })
    );
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setIsPage(0);
    setFilters(iniFilters);
    setAllFilters(iniAllFilter);
    setSearchFilterApplied(false);
  };

  const getCurrentCallFunction = () => {
    switch (activeTab) {
      case "categories":
        return () => getAddonsCategoriesData(filters);
      case "carry_items":
        return () => getCarryItemsData(filters);
      case "benefits":
        return () => getBenefitsData(filters);
      default:
        return () => getAddonsCategoriesData(filters);
    }
  };

  const getCurrentLoading = () => {
    switch (activeTab) {
      case "categories":
        return addonCategoriesLoading;
      case "carry_items":
        return addonCarryItemsLoading;
      case "benefits":
        return addonBenefitsLoading;
      default:
        return false;
    }
  };

  const getAddButtonText = () => {
    switch (activeTab) {
      case "categories":
        return "+ Add New Category";
      case "carry_items":
        return "+ Add Carry Item";
      case "benefits":
        return "+ Add Benefit";
      default:
        return "+ Add New";
    }
  };

  const getAddModalName = () => {
    switch (activeTab) {
      case "categories":
        return "add";
      case "carry_items":
        return "add_carry_item";
      case "benefits":
        return "add_benefit";
      default:
        return "add";
    }
  };

  // Fetch categories on mount (needed for dropdowns)
  useEffect(() => {
    getAddonsCategoriesData({ page: 1, limit: 100 });
  }, []);

  useEffect(() => {
    switch (activeTab) {
      case "categories":
        getAddonsCategoriesData(filters);
        break;
      case "carry_items":
        getCarryItemsData(filters);
        break;
      case "benefits":
        getBenefitsData(filters);
        break;
      default:
        break;
    }
  }, [filters, activeTab]);

  useEffect(() => {
    setFilters((prevs) => ({
      ...prevs,
      page: isPage + 1,
      limit: pageLimit || 10,
      status: allFilters.isActive?.value || "",
    }));
  }, [isPage, pageLimit, allFilters.isActive]);

  useEffect(() => {
    if (allFilters.searchString?.length === 0) {
      setSearchFilterApplied(false);
      setFilters((prevs) => ({ ...prevs, search: "" }));
      setIsPage(0);
    }
  }, [allFilters.searchString]);

  const renderModalContent = () => {
    const { modName, rowData } = modValues;

    // Description modal
    if (modName === "description") {
      return (
        <div
          className={`createNewCTOModalContainer mod-width-50p`}
        >
          <ModalHeadAndClose
            title={rowData?.name || " "}
            onClose={onCloseModal}
          />
          <div className="discussionModalMessage mt30">
            <p
              className="message"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  rowData?.description || "No Content Available"
                ),
              }}
            ></p>
          </div>
        </div>
      );
    }

    // View image modal
    if (modName === "view_image") {
      return (
        <div className={`createNewCTOModalContainer mod-width-50p`}>
          <ModalHeadAndClose
            title={modName?.replace(/_/g, " ")}
            onClose={onCloseModal}
          />
          <div className="discussionModalMessage mt30">
            <img
              src={rowData?.image || NoImage}
              alt="Category"
              style={{
                width: "400px",
                height: "400px",
              }}
            />
          </div>
        </div>
      );
    }

    // Category forms
    if (modName === "add" || modName === "edit") {
      return (
        <div className={`createNewCTOModalContainer mod-width-50p p-0`}>
          <AddonCategoryForm
            isEdit={modName === "edit"}
            rowData={rowData}
            closeModal={onCloseModal}
            userInfoData={userInfoData}
            callFunction={() => getAddonsCategoriesData(filters)}
          />
        </div>
      );
    }

    // Carry item forms
    if (modName === "add_carry_item" || modName === "edit_carry_item") {
      return (
        <div className={`createNewCTOModalContainer mod-width-50p p-0`}>
          <CarryItemForm
            isEdit={modName === "edit_carry_item"}
            rowData={rowData}
            closeModal={onCloseModal}
            userInfoData={userInfoData}
            callFunction={() => getCarryItemsData(filters)}
            categoryOptions={categoryOptions}
          />
        </div>
      );
    }

    // Benefits forms
    if (modName === "add_benefit" || modName === "edit_benefit") {
      return (
        <div className={`createNewCTOModalContainer mod-width-50p p-0`}>
          <BenefitsForm
            isEdit={modName === "edit_benefit"}
            rowData={rowData}
            closeModal={onCloseModal}
            userInfoData={userInfoData}
            callFunction={() => getBenefitsData(filters)}
            categoryOptions={categoryOptions}
          />
        </div>
      );
    }

    return null;
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "categories":
        return (
          <AddonCategoriesLists
            isPage={isPage}
            setIsPage={setIsPage}
            pageLimit={pageLimit}
            setPageLimit={setPageLimit}
            setModValues={setModValues}
            userInfoData={userInfoData}
            callFunction={() => getAddonsCategoriesData(filters)}
          />
        );
      case "carry_items":
        return (
          <CarryItemsList
            isPage={isPage}
            setIsPage={setIsPage}
            pageLimit={pageLimit}
            setPageLimit={setPageLimit}
            setModValues={setModValues}
            userInfoData={userInfoData}
            callFunction={() => getCarryItemsData(filters)}
          />
        );
      case "benefits":
        return (
          <BenefitsList
            isPage={isPage}
            setIsPage={setIsPage}
            pageLimit={pageLimit}
            setPageLimit={setPageLimit}
            setModValues={setModValues}
            userInfoData={userInfoData}
            callFunction={() => getBenefitsData(filters)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <HelmetWrapper title="WTF: Addons Categories Management">
      <Container className="bg-cloud-k px-50px gobal-container" fluid>
        <Modal
          isOpen={modValues.isModOpen}
          onRequestClose={onCloseModal}
          contentLabel="Detail Modal"
          ariaHideApp={false}
        >
          {renderModalContent()}
        </Modal>

        <Row>
          <Col>
            <CommonStructured
              pageHeading={"Addons Categories"}
              isReload={getCurrentCallFunction()}
              createBtnTxt={getAddButtonText()}
              onClickCreateBtn={() =>
                setModValues((prevs) => ({
                  ...prevs,
                  isModOpen: true,
                  modName: getAddModalName(),
                }))
              }
            />
          </Col>
        </Row>

        <Row>
          <Col>
            <div className="addon-tabs-container">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  className={`addon-tab-btn ${
                    activeTab === tab.id ? "active" : ""
                  }`}
                  onClick={() => handleTabChange(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </Col>
        </Row>

        <Row>
          <Col>
            <CmnFilters
              from="addons-categories"
              userInfoData={userInfoData}
              searchFilterApplied={searchFilterApplied}
              filters={allFilters}
              setFilters={setAllFilters}
              onClickSearch={onClickSearch}
              onClickReset={onResetFilters}
              isLoading={getCurrentLoading()}
              searchPlaceHolder={`Search by ${
                activeTab === "categories"
                  ? "category"
                  : activeTab === "carry_items"
                  ? "carry item"
                  : "benefit"
              } name...`}
            />
          </Col>
        </Row>

        <Row>
          <Col>{renderTabContent()}</Col>
        </Row>
      </Container>
    </HelmetWrapper>
  );
};

export default AddonsCategories;
